import React, { useCallback, useState, useRef } from 'react';
import { UploadCloud, FileText, X, File, FileCode, Presentation, FileSpreadsheet } from 'lucide-react';

interface DocumentUploadProps {
  label?: string;
  description?: string;
  accept?: string;
  maxFiles?: number;
  onChange?: (files: File[]) => void;
}

const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return <FileText size={20} className="text-red-500" />;
    case 'doc':
    case 'docx': return <FileText size={20} className="text-blue-500" />;
    case 'xls':
    case 'xlsx':
    case 'csv': return <FileSpreadsheet size={20} className="text-green-500" />;
    case 'ppt':
    case 'pptx': return <Presentation size={20} className="text-orange-500" />;
    case 'txt': return <FileText size={20} className="text-gray-500" />;
    case 'json':
    case 'xml': return <FileCode size={20} className="text-purple-500" />;
    default: return <File size={20} className="text-indigo-400" />;
  }
};

export function DocumentUpload({ label, description, accept = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt", maxFiles = 10, onChange }: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setIsDragging(true);
    else if (e.type === "dragleave") setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => {
        const updated = [...prev, ...newFiles].slice(0, maxFiles);
        if (onChange) onChange(updated);
        return updated;
      });
    }
  }, [maxFiles, onChange]);

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (onChange) onChange(updated);
      return updated;
    });
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {(label || description) && (
        <div>
          {label && <label className="text-sm font-medium text-gray-900">{label}</label>}
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      )}
      
      <div 
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col justify-center items-center w-full py-8 px-4 transition-all bg-white border-2 border-dashed rounded-xl cursor-pointer hover:border-indigo-400 focus:outline-none ${isDragging ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <UploadCloud className={`w-10 h-10 mb-3 ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`} />
        <span className="font-medium text-gray-700 text-sm mb-1">
            Arraste os documentos aqui ou <span className="text-indigo-600 underline">busque no PC</span>
        </span>
        <span className="text-xs text-gray-400">PDFs, Docs, Planilhas ou Apresentações (Max {maxFiles})</span>
        
        <input 
            ref={inputRef}
            type="file" 
            className="hidden" 
            accept={accept} 
            multiple={maxFiles > 1} 
            onChange={(e) => {
                if (e.target.files) {
                    const newFiles = Array.from(e.target.files);
                    setFiles(prev => {
                        const updated = [...prev, ...newFiles].slice(0, maxFiles);
                        if (onChange) onChange(updated);
                        return updated;
                    });
                }
            }} 
        />
      </div>

      {files.length > 0 && (
         <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
             {files.map((file, i) => (
                 <div key={i} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm group hover:border-gray-300 transition-colors">
                     <div className="w-10 h-10 flex-shrink-0 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-100">
                        {getFileIcon(file.name)}
                     </div>
                     <div className="flex-1 min-w-0">
                         <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>{file.name}</p>
                         <p className="text-xs text-gray-500 font-mono">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                     </div>
                     <button type="button" onClick={() => removeFile(i)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100">
                         <X size={16} />
                     </button>
                 </div>
             ))}
         </div>
      )}
    </div>
  );
}
